/**
 * Currency and date/time formatting — locale follows UI language (ar-JO / en-US).
 */
import i18n from '../i18n/config';

/** Map display symbols (as stored in site settings) → ISO 4217 for English UI. */
const SYMBOL_TO_ISO: Record<string, string> = {
  'د.أ': 'JOD',
  'ي.د': 'JOD',
  'ر.س': 'SAR',
  'د.ك': 'KWD',
  'د.إ': 'AED',
  'ج.م': 'EGP',
  $: 'USD',
  '€': 'EUR'
};

export function resolveFormatLocale(languageTag?: string): string {
  const raw = languageTag ?? i18n.language;
  const base = String(raw || '').split('-')[0].toLowerCase();
  return base === 'ar' ? 'ar-SA' : 'en-US';
}

export function currencyIsoCode(symbol: string): string {
  const s = (symbol || '').trim();
  return SYMBOL_TO_ISO[s] || s;
}

/**
 * Format currency: Arabic UI keeps the stored symbol; English UI uses ISO code + Western digits grouping.
 */
export const formatCurrency = (
  amount: number | string,
  currencySymbol: string = 'د.أ',
  languageTag?: string
): string => {
  const locale = resolveFormatLocale(languageTag);
  const sym = (currencySymbol && currencySymbol.trim()) ? currencySymbol.trim() : 'د.أ';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  if (isNaN(numAmount)) {
    if (locale.startsWith('ar')) {
      return `0 ${sym}`;
    }
    return `0 ${currencyIsoCode(sym)}`;
  }

  const formattedNumber = numAmount.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  if (locale.startsWith('ar')) {
    return `${formattedNumber} ${sym}`;
  }
  const code = currencyIsoCode(sym);
  return `${code} ${formattedNumber}`;
};

export const formatDateTime = (
  date: Date | string,
  options: {
    includeTime?: boolean;
    includeSeconds?: boolean;
    shortFormat?: boolean;
    languageTag?: string;
  } = {}
): string => {
  const {
    includeTime = true,
    includeSeconds = false,
    shortFormat = false,
    languageTag
  } = options;

  const locale = resolveFormatLocale(languageTag);
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return i18n.t('format.invalidDate');
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: shortFormat ? 'short' : 'long',
    day: 'numeric',
    calendar: 'gregory'
  };

  let formattedDate = dateObj.toLocaleDateString(locale, dateOptions);

  if (includeTime) {
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      ...(includeSeconds && { second: '2-digit' }),
      hour12: true
    };
    const sep = locale.startsWith('ar') ? '، ' : ', ';
    const formattedTime = dateObj.toLocaleTimeString(locale, timeOptions);
    formattedDate += `${sep}${formattedTime}`;
  }

  return formattedDate;
};

export const formatDate = (date: Date | string, shortFormat: boolean = false, languageTag?: string): string => {
  return formatDateTime(date, { includeTime: false, shortFormat, languageTag });
};

export const formatTime = (date: Date | string, includeSeconds: boolean = false, languageTag?: string): string => {
  const locale = resolveFormatLocale(languageTag);
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return i18n.t('format.invalidTime');
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    ...(includeSeconds && { second: '2-digit' }),
    hour12: true
  };

  return dateObj.toLocaleTimeString(locale, timeOptions);
};

export const formatRemainingTime = (endDate: Date | string): string => {
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
  const now = new Date();
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return i18n.t('format.ended');
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  if (days > 0) {
    return i18n.t('format.daysHours', { days, hours });
  }
  if (hours > 0) {
    return i18n.t('format.hoursMinutes', { hours, minutes });
  }
  if (minutes > 0) {
    return i18n.t('format.minutesSeconds', { minutes, seconds });
  }
  return i18n.t('format.secondsOnly', { seconds });
};

export const hijriToGregorian = (hijriDate: string): Date => {
  try {
    const date = new Date(hijriDate);
    return isNaN(date.getTime()) ? new Date() : date;
  } catch {
    return new Date();
  }
};
